import { createContext, useContext, type ReactNode } from "react";
import type { TransporterProfileDto } from "../lib/fetch-transporter-delivery-ops";
import type { OnboardingRecordDto } from "../lib/fetch-transporter-onboarding";

export type TransporterWorkspaceValue = {
  transporterId: string | null;
  getToken: () => Promise<string | null>;
  /**
   * Incremented after debounced Socket.io activity (**`/ws/transporter/:id`**). **Fleet** refetches when this
   * changes (**TB-060-H2**).
   */
  fleetRefreshNonce: number;
  /** Latest profile from **`GET /api/v1/transporters/:id`** (workspace scope). */
  workspaceProfile: TransporterProfileDto | null;
  /** Onboarding step rows from **`GET …/onboarding`**. */
  onboardingRecords: OnboardingRecordDto[] | null;
  /** True while the first workspace profile + onboarding fetch is in flight. */
  workspaceProfileLoading: boolean;
  /** Refetch profile + onboarding (e.g. after completing onboarding). */
  refreshWorkspaceProfile: () => Promise<void>;
};

const TransporterWorkspaceContext = createContext<TransporterWorkspaceValue | null>(null);

export function TransporterWorkspaceProvider({
  value,
  children,
}: {
  value: TransporterWorkspaceValue;
  children: ReactNode;
}) {
  return (
    <TransporterWorkspaceContext.Provider value={value}>
      {children}
    </TransporterWorkspaceContext.Provider>
  );
}

export function useTransporterWorkspace(): TransporterWorkspaceValue {
  const v = useContext(TransporterWorkspaceContext);
  if (!v) {
    throw new Error("useTransporterWorkspace must be used within TransporterWorkspaceProvider");
  }
  return v;
}
